/**
 * /vendors/:vendorId/autopilot — Per-vendor AP Autopilot configuration.
 *
 * Sections:
 *   - Mode toggle: Shadow (log only) vs Live (auto-post)
 *   - Confidence threshold
 *   - Per-vendor ceiling
 *   - Allow uncorroborated (SaaS)
 *   - Allow PO-less (construction only)
 *   - Stats: total processed, auto-posted, auto-post rate
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Chip,
  CircularProgress,
  TextField,
} from "@mui/material";
import { MoneyField } from "@/components/MoneyField";
import { myEntitiesQueryOptions } from "@/lib/queries";
import { api } from "@/lib/api";

export const Route = createFileRoute("/_auth/vendors/$vendorId/autopilot")({
  component: VendorAutopilotPage,
});

// ─── types ───────────────────────────────────────────────────────────────────

interface VendorPreference {
  vendor_id: string;
  entity_id: string;
  auto_post_threshold: string | null;
  auto_post_ceiling: string | null;
  allow_uncorroborated: boolean;
  allow_po_less: boolean;
  auto_post_mode: string;
  vendor_watch_slug: string | null;
  expected_currency: string | null;
  expected_terms: string | null;
  default_account_id: string | null;
  notes: string | null;
  updated_at: string;
}

interface AutopilotStats {
  total_processed: number;
  auto_posted_count: number;
  awaiting_review_count: number;
  auto_post_rate: number;
  shadow_would_have_posted_count: number;
  _note?: string;
}

interface FormState {
  auto_post_mode: "shadow" | "live";
  auto_post_threshold: string;
  auto_post_ceiling: string;
  allow_uncorroborated: boolean;
  allow_po_less: boolean;
  vendor_watch_slug: string;
}

// Known vendor-watch slugs (SaaS spend adapters that ship in v1).
// Kept in code — the source of truth is VendorSpendAccount.vendor.
const WATCH_SLUG_OPTIONS = [
  { value: "", label: "None (no corroboration)" },
  { value: "anthropic", label: "Anthropic" },
  { value: "aws", label: "AWS" },
  { value: "stripe", label: "Stripe" },
  { value: "github", label: "GitHub" },
  { value: "vercel", label: "Vercel" },
  { value: "openai", label: "OpenAI" },
  { value: "cursor", label: "Cursor" },
  { value: "vapi", label: "Vapi" },
  { value: "cloudflare", label: "Cloudflare" },
];

// ─── helpers ─────────────────────────────────────────────────────────────────

function prefToForm(pref: VendorPreference): FormState {
  return {
    auto_post_mode: (pref.auto_post_mode === "live" ? "live" : "shadow") as "shadow" | "live",
    auto_post_threshold: pref.auto_post_threshold ?? "0.90",
    auto_post_ceiling: pref.auto_post_ceiling ?? "",
    allow_uncorroborated: pref.allow_uncorroborated,
    allow_po_less: pref.allow_po_less,
    vendor_watch_slug: pref.vendor_watch_slug ?? "",
  };
}

// ─── page ─────────────────────────────────────────────────────────────────────

function VendorAutopilotPage() {
  const { vendorId } = Route.useParams();
  const qc = useQueryClient();

  // Construction pack gate
  const entitiesQuery = useQuery(myEntitiesQueryOptions());
  const currentEntity = entitiesQuery.data?.find((e) => e.is_current);
  const constructionEnabled = currentEntity?.construction_enabled ?? false;

  // Fetch preferences
  const prefQuery = useQuery({
    queryKey: ["vendor-autopilot-pref", vendorId],
    queryFn: () => api<VendorPreference>(`/api/ap/intake/vendors/${vendorId}/preferences`),
    staleTime: 30_000,
  });

  // Fetch stats
  const statsQuery = useQuery({
    queryKey: ["vendor-autopilot-stats", vendorId],
    queryFn: () => api<AutopilotStats>(`/api/ap/intake/vendors/${vendorId}/autopilot/stats`),
    staleTime: 60_000,
  });

  const [form, setForm] = useState<FormState>({
    auto_post_mode: "shadow",
    auto_post_threshold: "0.90",
    auto_post_ceiling: "",
    allow_uncorroborated: false,
    allow_po_less: false,
    vendor_watch_slug: "",
  });
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Sync form when preference data arrives
  useEffect(() => {
    if (prefQuery.data) {
      setForm(prefToForm(prefQuery.data));
    }
  }, [prefQuery.data]);

  const saveMut = useMutation({
    mutationFn: () =>
      api<VendorPreference>(`/api/ap/intake/vendors/${vendorId}/preferences`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          auto_post_mode: form.auto_post_mode,
          auto_post_threshold: form.auto_post_threshold ? parseFloat(form.auto_post_threshold) : null,
          auto_post_ceiling: form.auto_post_ceiling ? parseFloat(form.auto_post_ceiling) : null,
          allow_uncorroborated: form.allow_uncorroborated,
          allow_po_less: form.allow_po_less,
          vendor_watch_slug: form.vendor_watch_slug || null,
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendor-autopilot-pref", vendorId] });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  const isLive = form.auto_post_mode === "live";

  return (
    <div className="flex flex-col bg-slate-50 dark:bg-gray-950 overflow-auto min-h-full">
      {/* sub-header */}
      <div className="bg-white border-b border-slate-200 dark:bg-gray-900 dark:border-gray-800 px-6 py-3 flex items-center gap-3">
        <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">AP Autopilot</span>
        {prefQuery.isLoading && <CircularProgress size={14} />}
      </div>

      {prefQuery.isError && (
        <div className="m-6 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-300">
          {(prefQuery.error as Error).message}
        </div>
      )}

      <div className="p-6 space-y-6 max-w-2xl">
        {/* Settings card */}
        <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-5">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">
            Autopilot settings
          </h2>

          {/* Mode toggle */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">
              Mode
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, auto_post_mode: "shadow" }))}
                className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                  !isLive
                    ? "bg-slate-700 text-white border-slate-700 dark:bg-slate-600 dark:border-slate-600"
                    : "border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-gray-700 dark:text-slate-300 dark:hover:bg-gray-800"
                }`}
              >
                Shadow (log only)
              </button>
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, auto_post_mode: "live" }))}
                className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                  isLive
                    ? "bg-orange-600 text-white border-orange-600"
                    : "border-slate-300 text-slate-600 hover:bg-slate-50 dark:border-gray-700 dark:text-slate-300 dark:hover:bg-gray-800"
                }`}
              >
                Live (auto-post)
              </button>
            </div>

            {/* Mode explanation chip */}
            <div className="mt-2">
              {!isLive ? (
                <div className="flex items-start gap-2 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-md px-3 py-2">
                  <span className="text-xs text-blue-700 dark:text-blue-300">
                    Autopilot logs what it would do but never posts. Switch to Live when you trust the data.
                  </span>
                </div>
              ) : (
                <div className="flex items-start gap-2 bg-orange-50 dark:bg-orange-950 border border-orange-200 dark:border-orange-800 rounded-md px-3 py-2">
                  <span className="text-xs text-orange-700 dark:text-orange-300">
                    Live mode will post invoices directly to your GL without review. Ensure your confidence threshold and ceiling are set appropriately.
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Confidence threshold */}
          <div className="mb-4">
            <TextField
              size="small"
              label="Minimum confidence score"
              type="number"
              value={form.auto_post_threshold}
              onChange={(e) => setForm((f) => ({ ...f, auto_post_threshold: e.target.value }))}
              inputProps={{ min: 0.80, max: 0.99, step: 0.01 }}
              helperText="Weakest-link of parse confidence × match confidence. Range 0.80–0.99."
              fullWidth
            />
          </div>

          {/* Ceiling */}
          <div className="mb-4">
            <MoneyField
              size="small"
              label="Auto-post ceiling"
              value={form.auto_post_ceiling}
              onChange={(v) => setForm((f) => ({ ...f, auto_post_ceiling: v }))}
              helperText="Leave blank for no cap. Bills above this amount always go to review."
              fullWidth
            />
          </div>

          {/* Vendor-watch corroboration slug (SaaS P1) */}
          <div className="mb-4">
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">
              Vendor-watch corroboration
            </label>
            <select
              value={form.vendor_watch_slug}
              onChange={(e) => setForm((f) => ({ ...f, vendor_watch_slug: e.target.value }))}
              className="w-full rounded-md border border-slate-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm text-slate-700 dark:text-slate-300 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              {WATCH_SLUG_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Match bills against your vendor-spend watch data. The bill must reconcile
              within 5% of the projected MTD spend to auto-post.
            </p>
          </div>

          {/* Allow uncorroborated (SaaS) */}
          <div className="mb-3">
            <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={form.allow_uncorroborated}
                onChange={(e) => setForm((f) => ({ ...f, allow_uncorroborated: e.target.checked }))}
                className="w-4 h-4 mt-0.5 flex-shrink-0"
              />
              <span>
                Allow auto-post without vendor-watch match{" "}
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  (subscription vendors — use when you trust the vendor and bills are stable)
                </span>
              </span>
            </label>
          </div>

          {/* Allow PO-less (construction only) */}
          {constructionEnabled && (
            <div className="mb-3">
              <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.allow_po_less}
                  onChange={(e) => setForm((f) => ({ ...f, allow_po_less: e.target.checked }))}
                  className="w-4 h-4 mt-0.5 flex-shrink-0"
                />
                <span>
                  Allow auto-post without a matched PO{" "}
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    (recurring GL-only vendors — narrow exception for e.g. equipment rental)
                  </span>
                </span>
              </label>
            </div>
          )}

          {/* Construction hard-blocks explainer (construction entities only) */}
          {constructionEnabled && (
            <ConstructionBlocksExplainer />
          )}

          {/* Save */}
          <div className="flex items-center gap-3 mt-5 pt-4 border-t border-slate-100 dark:border-gray-800">
            <button
              type="button"
              disabled={saveMut.isPending}
              onClick={() => saveMut.mutate()}
              className="text-sm px-4 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {saveMut.isPending ? "Saving…" : "Save"}
            </button>
            {saveSuccess && (
              <span className="text-xs text-emerald-600 dark:text-emerald-400">
                Saved
              </span>
            )}
            {saveMut.isError && (
              <span className="text-xs text-red-600 dark:text-red-400">
                {(saveMut.error as Error).message}
              </span>
            )}
          </div>
        </div>

        {/* P3 Readiness card */}
        <ReadinessCard vendorId={vendorId} onApplyThreshold={(t) => setForm((f) => ({ ...f, auto_post_threshold: t, auto_post_mode: "shadow" }))} />

        {/* Stats card */}
        <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-5">
          <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">
            Last 90 days
          </h2>

          {statsQuery.isLoading && <CircularProgress size={16} />}

          {statsQuery.data && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-4">
                <StatBox label="Total processed" value={statsQuery.data.total_processed} />
                <StatBox label="Auto-posted" value={statsQuery.data.auto_posted_count} />
                <StatBox
                  label="Auto-post rate"
                  value={`${statsQuery.data.auto_post_rate}%`}
                  highlight={statsQuery.data.auto_post_rate > 0}
                />
              </div>

              {statsQuery.data.awaiting_review_count > 0 && (
                <div className="flex items-center gap-2 pt-1">
                  <Chip
                    label={`${statsQuery.data.awaiting_review_count} awaiting review`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                </div>
              )}

              <p className="text-xs text-slate-400 dark:text-slate-500 pt-1">
                Shadow "would-have-posted" count is tracked in structured logs — contact support to view.
              </p>
            </div>
          )}

          {statsQuery.isError && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {(statsQuery.error as Error).message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── micro components ─────────────────────────────────────────────────────────

function StatBox({ label, value, highlight }: { label: string; value: string | number; highlight?: boolean }) {
  return (
    <div className="bg-slate-50 dark:bg-gray-800 rounded-lg p-3 text-center">
      <div className={`text-xl font-semibold ${highlight ? "text-blue-600 dark:text-blue-400" : "text-slate-700 dark:text-slate-300"}`}>
        {value}
      </div>
      <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{label}</div>
    </div>
  );
}

// ─── P3 readiness card ────────────────────────────────────────────────────────

interface ReadinessData {
  vendor_id: string;
  sample_size: number;
  recommended_threshold: string | null;
  ready_for_live: boolean;
  no_touch_rate: string | null;
  wrong_post_count: number;
  agreement_rate: string;
  explanation: string;
  sweep: Array<{
    threshold: string;
    true_positives: number;
    false_positives: number;
    no_touch_rate: string;
    precision: string;
  }>;
  lookback_days: number;
}

function ReadinessCard({
  vendorId,
  onApplyThreshold,
}: {
  vendorId: string;
  onApplyThreshold: (threshold: string) => void;
}) {
  const readinessQuery = useQuery({
    queryKey: ["vendor-autopilot-readiness", vendorId],
    queryFn: () => api<ReadinessData>(`/api/ap/intake/vendors/${vendorId}/autopilot/readiness`),
    staleTime: 5 * 60_000,
  });

  const r = readinessQuery.data;

  return (
    <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300">
          Autopilot readiness
        </h2>
        {readinessQuery.isLoading && <CircularProgress size={14} />}
        {r && (
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              r.ready_for_live
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300"
                : r.sample_size >= 10
                ? "bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300"
                : "bg-slate-100 text-slate-500 dark:bg-gray-800 dark:text-slate-400"
            }`}
          >
            {r.ready_for_live
              ? "Ready for live"
              : r.sample_size >= 10
              ? "Building history"
              : "Needs data"}
          </span>
        )}
      </div>

      {r && r.sample_size === 0 && (
        <p className="text-sm text-slate-500 dark:text-slate-400">{r.explanation}</p>
      )}

      {r && r.sample_size > 0 && (
        <div className="space-y-3">
          {/* Key metrics */}
          <div className="grid grid-cols-3 gap-3">
            <StatBox label="Bills analyzed" value={r.sample_size} />
            <StatBox
              label="Agreement rate"
              value={`${Math.round(parseFloat(r.agreement_rate) * 100)}%`}
              highlight={parseFloat(r.agreement_rate) >= 0.9}
            />
            <StatBox
              label="Wrong posts"
              value={r.wrong_post_count}
              highlight={r.wrong_post_count === 0}
            />
          </div>

          {/* Explanation */}
          <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
            {r.explanation}
          </p>

          {/* Precision bar chart (top 5 thresholds) */}
          {r.sweep.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">
                Threshold sweep — no-touch rate at each confidence level
              </p>
              <div className="space-y-1">
                {r.sweep
                  .filter((_, i) => i % 4 === 0)  // sample every 4th threshold for readability
                  .map((row) => {
                    const noTouchPct = Math.round(parseFloat(row.no_touch_rate) * 100);
                    const precisionPct = Math.round(parseFloat(row.precision) * 100);
                    const isRecommended = r.recommended_threshold === row.threshold;
                    return (
                      <div key={row.threshold} className="flex items-center gap-2">
                        <span className={`text-xs w-8 text-right font-mono ${isRecommended ? "text-blue-600 dark:text-blue-400 font-bold" : "text-slate-500"}`}>
                          {row.threshold}
                        </span>
                        <div className="flex-1 h-2 bg-slate-100 dark:bg-gray-800 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${row.false_positives > 0 ? "bg-red-400" : isRecommended ? "bg-blue-500" : "bg-slate-400"}`}
                            style={{ width: `${noTouchPct}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500 w-12">
                          {noTouchPct}% / {precisionPct}%
                        </span>
                        {isRecommended && (
                          <span className="text-xs text-blue-600 dark:text-blue-400">←</span>
                        )}
                      </div>
                    );
                  })}
                <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                  Bar = no-touch rate · number = no-touch% / precision%
                  {r.sweep.some((row) => row.false_positives > 0) && (
                    <span className="text-red-500 ml-1">· red = wrong posts at this threshold</span>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Apply button */}
          {r.recommended_threshold && (
            <div className="pt-2 border-t border-slate-100 dark:border-gray-800">
              <button
                type="button"
                onClick={() => onApplyThreshold(r.recommended_threshold!)}
                className="text-sm px-3 py-1.5 rounded border border-blue-500 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950"
              >
                Apply recommended threshold ({r.recommended_threshold})
              </button>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                Sets the threshold above. Save to confirm. Mode stays Shadow until you switch.
              </p>
            </div>
          )}
        </div>
      )}

      {readinessQuery.isError && (
        <p className="text-sm text-red-600 dark:text-red-400">
          {(readinessQuery.error as Error).message}
        </p>
      )}
    </div>
  );
}

// ─── Construction blocks explainer ───────────────────────────────────────────

const CONSTRUCTION_BLOCKS = [
  {
    code: "po_required",
    label: "No open PO",
    resolution: "Create or approve a PO for this vendor, or enable 'Allow PO-less' above for GL-only recurring charges (e.g. equipment rental).",
  },
  {
    code: "po_tolerance_exceeded",
    label: "Over PO balance",
    resolution: "Invoice total exceeds the PO's remaining balance. Create a change order or adjust the PO before this bill can auto-post.",
  },
  {
    code: "no_goods_receipt",
    label: "No goods receipt",
    resolution: "Post a receipt in Purchase Orders → Receipts to confirm delivery. The 3-way match requires PO + receipt + invoice.",
  },
  {
    code: "expired_coi",
    label: "COI / qualification expired",
    resolution: "Renew the vendor's certificate of insurance in Vendor Qualifications. Auto-posting resumes once qualification status is Approved or Conditional.",
  },
  {
    code: "compliance_gate_fail",
    label: "Open compliance alert",
    resolution: "Resolve the critical compliance alert for this vendor (union, certified payroll, or Davis-Bacon) in the Compliance section before auto-posting resumes.",
  },
  {
    code: "retention_line",
    label: "Retention line",
    resolution: "Invoices with a retention component always require manual review. Retention release is a project-level decision, not an accounting match.",
  },
  {
    code: "sub_pay_app",
    label: "Sub pay application",
    resolution: "Schedule-of-values draws always require manual review. Remove this vendor from autopilot or keep in Shadow mode.",
  },
  {
    code: "lien_waiver_outstanding",
    label: "Outstanding lien waiver",
    resolution: "Mark the outstanding lien waiver request as Received or Verified before auto-posting resumes for this vendor.",
  },
] as const;

function ConstructionBlocksExplainer() {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 border border-slate-200 dark:border-gray-700 rounded-md">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-gray-800 rounded-md"
      >
        <span>Construction hard blocks — what can prevent auto-posting</span>
        <span>{open ? "▴" : "▾"}</span>
      </button>
      {open && (
        <div className="border-t border-slate-100 dark:border-gray-800 px-3 py-3 space-y-2.5">
          <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
            These blocks fire before the confidence threshold is checked. They are always hard —
            no flag can bypass them. Resolve the underlying issue, then resubmit the bill.
          </p>
          {CONSTRUCTION_BLOCKS.map((b) => (
            <div key={b.code} className="text-xs">
              <span className="font-medium text-slate-700 dark:text-slate-300">{b.label}</span>
              <span className="font-mono text-slate-400 dark:text-slate-500 ml-1.5 text-[10px]">
                [{b.code}]
              </span>
              <p className="text-slate-500 dark:text-slate-400 mt-0.5 leading-relaxed">
                {b.resolution}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
